/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Copyright (c) 2026, Ouster, Inc.
 * All rights reserved.
 *
 */
import { DataMessage } from '@ouster/webviz';
import {
  CircularProgress,
  PlayBar,
  ReplayStreamInfo,
  SensorSectionInfo,
  VizLeftSidebar,
  Flex,
  Button,
  Icon,
  SensorInfo,
  SensorConfig,
  ShortcutKeyHandler,
  Box,
  VizFooter,
  ModalContainer,
  useDisclosure,
  azimuthWindowFromColumnWindow,
} from '@ouster/webviz-react-components';
import type {
  ImageSettings,
  FilesInfo,
  CreatedOption,
  PointCloudSensorSettings,
} from '@ouster/webviz-react-components';

import {
  isEmpty,
  isUndefined,
  throttle,
  uniq,
  isEqual,
  debounce as lodashDebounce,
} from 'lodash';
import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  ComponentProps,
  useMemo,
} from 'react';
import { isMacOs } from 'react-device-detect';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Subject } from 'rxjs';
import {
  frameCache,
  selectPointCloudGlobalSettings,
  selectPointCloudSensorsSettings,
  selectSelectedSensorImageSettings,
} from 'src/reducers/vizScene';
import { normalizeSensorInfoFromWorker } from 'src/utils/driveUtil';

import { ContainerBelowNav } from './NavBar';
import { VizPage, VizPageRef } from './VizPage';
import { DriveNavBar } from '../components/DriveNavBar';
import { getOSFWorker } from '../getOSFWorker';
import useChakraHook from '../hooks/useChakraHook';
import useIsMobileViewport, {
  isMobileDevice,
  useViewportOrientation,
} from '../hooks/useIsMobileViewport';
import { useSentryErrorTracker } from '../hooks/useSentryErrorTracker';
import { useStoreState, useStoreActions } from '../reducers';
import { DriveOsfInfo, getSectionIdFromGlobalFrame } from '../reducers/drive'; // NOTE: sectionId has replaced bagId
import {
  Tag,
  MappedTag,
  OSFType,
  Status,
  SDKSensorInfoAugmented,
} from '../types';
import { GCS_BASE_URL } from '../utils/constants';
import {
  usePrevious,
  useAnimationFrameThrottled,
  useFeedbackForm,
} from '../utils/customHooks';
import { mod } from '../utils/math';
import {
  formatTag,
  mapData,
  debounce,
  frequencyFromLidarMode,
} from '../utils/misc';
import { getSignedUrl } from '../utils/requests';
import { isAdminLevelRole } from '../utils/roles';
import {
  updateFrameInUrl,
  updateSceneInUrl,
  decodeSceneState,
} from '../utils/searchParams';
import { createDriveViewerShortcuts } from '../utils/shortcuts';

type CameraShareState = {
  cameraType: 'orthographic' | 'perspective';
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
};

type SceneShareState = {
  camera?: CameraShareState;
  pointSize?: number;
  showRangeRings?: boolean;
  is2DImageVisible?: boolean;
  leftSidebarExpanded?: boolean;
  selectedImagePanelSn?: string;
  selectedSensorImageSettings?: Array<{
    showImage?: boolean;
    coloringMode?: string;
    colorPalette?: string;
    isAutoLevelsOn?: boolean;
    isNoiseSuppressionOn?: boolean;
  }>;
};

const decodeSceneStateSafe = decodeSceneState as (
  encodedSceneState: string | null
) => SceneShareState | null;

const updateSceneInUrlSafe = updateSceneInUrl as (
  scenePatch: Partial<SceneShareState>
) => void;

const defaultImagePaletteForMode = (
  coloringMode?: string
): string | undefined => {
  if (!coloringMode) return undefined;
  if (coloringMode === 'RGB') return 'RGB';
  if (coloringMode.startsWith('REFLECTIVITY')) return 'CalRef';
  return 'Greyscale';
};

const toSharedImageSettings = (
  settings: ImageSettings[],
  userAutoLevelsByImageIndex?: Record<number, boolean>
): NonNullable<SceneShareState['selectedSensorImageSettings']> =>
  settings.map((setting, index) => {
    const mode =
      typeof setting.coloringMode === 'string'
        ? setting.coloringMode
        : String(setting.coloringMode ?? '');
    const defaultPalette = defaultImagePaletteForMode(mode);
    const includePalette =
      typeof setting.colorPalette === 'string' &&
      setting.colorPalette.length > 0 &&
      setting.colorPalette !== defaultPalette;

    return {
      showImage: setting.showImage,
      coloringMode: setting.coloringMode,
      ...(includePalette ? { colorPalette: setting.colorPalette } : {}),
      ...(setting.isAutoLevelsDisabled ||
      !Object.prototype.hasOwnProperty.call(
        userAutoLevelsByImageIndex ?? {},
        index
      )
        ? {}
        : { isAutoLevelsOn: setting.isAutoLevelsOn }),
      ...(setting.isNoiseSuppressionDisabled
        ? {}
        : { isNoiseSuppressionOn: setting.isNoiseSuppressionOn }),
    };
  });

const toPartialImageSettings = (
  setting: NonNullable<SceneShareState['selectedSensorImageSettings']>[number]
): Partial<ImageSettings> => ({
  ...(setting.showImage !== undefined ? { showImage: setting.showImage } : {}),
  ...(setting.coloringMode !== undefined
    ? { coloringMode: setting.coloringMode }
    : {}),
  ...(setting.colorPalette !== undefined
    ? {
        colorPalette: setting.colorPalette as
          | ImageSettings['colorPalette']
          | undefined,
      }
    : {}),
  ...(setting.isAutoLevelsOn !== undefined
    ? { isAutoLevelsOn: setting.isAutoLevelsOn }
    : {}),
  ...(setting.isNoiseSuppressionOn !== undefined
    ? { isNoiseSuppressionOn: setting.isNoiseSuppressionOn }
    : {}),
});

type WorkerSensorInfoPayload = Record<string, unknown>;

const isValidAzimuthWindow = (value?: number[]): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((entry) => typeof entry === 'number') &&
  value.some((entry) => Math.abs(entry) > 360);

type WorkerMessage =
  | {
      frameIdx: number;
      dataMessages?: DataMessage[];
      ts?: number;
      reqEpoch?: number;
    }
  | { loadedFrames: number[] }
  | { framesCount: number; estimatedFps: number }
  | { calculatedFps: number }
  | {
      command: 'init';
      sensorInfo?: WorkerSensorInfoPayload | WorkerSensorInfoPayload[];
      allSensorsInfo?: WorkerSensorInfoPayload[];
    }
  | { command: 'renewUrl'; reason: string }
  | { command: 'error'; message: string | object };

const isValidWorkerFps = (value: number): boolean =>
  Number.isFinite(value) && value > 0 && value <= 240;

const shouldUseWorkerFps = (
  workerFps: number | undefined,
  lidarModeFrequency: number
): workerFps is number => {
  if (workerFps === undefined || !isValidWorkerFps(workerFps)) {
    return false;
  }

  if (!Number.isFinite(lidarModeFrequency) || lidarModeFrequency <= 0) {
    return true;
  }

  // Keep worker fps when it is reasonably close to lidar-mode frequency.
  // Older recordings can report implausibly low values (for example 2.5 vs 20).
  const ratio = workerFps / lidarModeFrequency;
  return ratio >= 0.35 && ratio <= 2.5;
};

// How often we check on state of the processes and reload [ms]
const DRIVE_REFRESH_DELAY = 5000;
const SEEK_TRANSITION_SPINNER_DELAY_MS = 140;
const KEYBOARD_SEEK_TARGET_TOLERANCE = 2;
const MANUAL_SEEK_ACTIVE_WINDOW_MS = 300;
const CAMERA_URL_UPDATE_DEBOUNCE_MS = 400;
const SCENE_SETTING_URL_UPDATE_DEBOUNCE_MS = 300;
const SIDEBAR_TRANSITION_FALLBACK_MS = 350;

type LoaderContainerProps = React.ComponentProps<typeof Box>;
const LoaderContainer = (props: LoaderContainerProps) => (
  <Box
    height="100%"
    width="100%"
    display="flex"
    alignItems="center"
    justifyContent="center"
    minH="50vh"
    position="fixed"
    top="0"
    bg="var(--color-unify-darker-blue)"
    {...props}
  />
);

const FeedbackFormContainer: React.FC<{
  formLink: string;
  children?: React.ReactNode;
}> = ({ formLink, children, ...rest }) => (
  <Box
    as="a"
    href={formLink}
    target="_blank"
    rel="noopener noreferrer"
    color="var(--color-unify-primary, inherit)"
    _hover={{ filter: 'brightness(130%)' }}
    {...(rest as any)}
  >
    {children}
  </Box>
);

const VizInfoMessageContainer: React.FC<{ children?: React.ReactNode }> = ({
  children,
  ...rest
}) => (
  <Box
    alignSelf="center"
    maxW="68ch"
    p="56px"
    height="100%"
    fontWeight={500}
    fontSize="18px"
    lineHeight="1.8"
    display="grid"
    placeItems="center"
    whiteSpace="pre-wrap"
    sx={{ em: { color: 'var(--color-nebula-yellow)' } }}
    {...(rest as any)}
  >
    {children}
  </Box>
);

const SliderControlsContainer: React.FC<{ children?: React.ReactNode }> = ({
  children,
  ...rest
}) => (
  <Box
    flex="0 0 64px"
    bg="#313841"
    display="flex"
    flexDirection="row"
    {...(rest as any)}
  >
    {children}
  </Box>
);

// When the user drags the playbar thumb, we want to update the frame
// index in the URL
const onSliderChange = throttle((values: ReadonlyArray<number>) => {
  const sliderValue = Math.floor(values[0]);
  if (sliderValue === undefined || Number.isNaN(sliderValue)) {
    console.warn(`Slider value has to be a number, got ${sliderValue}`);
  } else {
    updateFrameInUrl(sliderValue);
  }
}, 75);

const osfTypeFromString = (osfConfigMode: string) => {
  switch (osfConfigMode) {
    case 'osf':
      return OSFType.legacyOsf;
    case 'binary-osf':
      return OSFType.osf;
    default:
      throw new Error(`OSF mode ${osfConfigMode} is not supported`);
  }
};

const extractOSFType = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfTypeFromString(osfInfo.configById[sectionId].mode);

const extractOSFPath = (osfInfo: DriveOsfInfo, sectionId: string) =>
  `${sectionId}/${osfInfo.configById[sectionId].dataPath}/${sectionId}.osf`;

const extractOSFOffset = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfInfo.offsetsById[sectionId];

// Note that in the current 'old' web-viz, the data loader and its
// frame manager (which eventually controls the 'loadedLocalFrames') only
// deal with a single OSF file at a time, so they won't buffer data
// between multiple files, which means that there will be no buffering
// at the junction of files
const getMaxLoadedLocalFrame = (
  currentLocalFrame: number,
  loadedLocalFrames: number[]
) => {
  let nextConsecutive = 0;
  if (loadedLocalFrames && loadedLocalFrames.length > 0) {
    // Get max index for which every number exists between min and max
    nextConsecutive = currentLocalFrame;
    const loadedFramesSet = new Set(loadedLocalFrames);
    const maxInd = Math.max(...loadedLocalFrames);
    while (nextConsecutive < maxInd) {
      if (!loadedFramesSet.has(nextConsecutive)) {
        break;
      }
      nextConsecutive += 1;
    }
  }
  return nextConsecutive;
};

/* 
  Help method to adjust frame rate based take image number and resolution into consideration
*/
const calculateAdjustedFrameRate = (
  baseFrameRate: number,
  imageCount: number,
  speed: number
): number => {
  // Image factor with a more conservative scaling
  // const imageFactor = 1.2 ** (imageCount - 1);

  // Speed factor: progressively scales, capped at a maximum multiplier of 4
  // we usually do good on speed 1, but when the number go up
  // it seems we need to increase the speed to hit the desired number
  const speedFactor = Math.min(speed ** 1.1, 4);

  const adjustedFrameRate = baseFrameRate * speedFactor;

  return Math.round(adjustedFrameRate);
};

const DriveViewer = React.memo(() => {
  const { showToast } = useChakraHook();
  const leftSidebarRef = useRef<HTMLDivElement>(null);
  const sceneContainerRef = useRef<HTMLDivElement>(null);
  const vizPageRef = useRef<VizPageRef>(null);
  // Local State
  const [frameTs, setFrameTs] = useState<number | undefined>(undefined);
  const [loadedLocalFrames, setLoadedLocalFrames] = useState<number[]>([]);
  const [hoverMode, setHoverMode] = useState(false);
  const [isVizInitialized, setIsVizInitialized] = useState(false);
  const [replayFPS, setWorkerFps] = useState<number | undefined>(undefined);
  // const [shouldZoomIn, setShouldZoomIn] = useState(false);
  // const [shouldZoomOut, setShouldZoomOut] = useState(false);

  // Grab information from the URL
  const { orgId, driveId, publishedDriveUuid } = useParams<{
    orgId?: string;
    driveId?: string;
    publishedDriveUuid?: string;
  }>();
  const [searchParams] = useSearchParams();
  const isMobileViewport = useIsMobileViewport();
  const viewportOrientation = useViewportOrientation();
  const isMobilePortrait =
    isMobileViewport && viewportOrientation === 'portrait';
  const routeFrameIdxString = searchParams.get('frame');
  const routeFrameIdx = routeFrameIdxString ? +routeFrameIdxString : undefined;
  const routeSceneState = useMemo(
    () => decodeSceneStateSafe(searchParams.get('scene')),
    [searchParams]
  );
  const routeSceneParam = searchParams.get('scene');

  // Reducer state
  const loadStatus = useStoreState((state) => state.drive.loadStatus);
  const numFrames = useStoreState((state) => state.drive.numFrames);
  const currentFrameIdx = useStoreState((state) => state.drive.currentFrameIdx);
  const lastReceivedFrameIdx = useStoreState(
    (state) => state.drive.lastReceivedFrameIdx
  );
  const seeking = useStoreState((state) => state.drive.seeking);
  const frameStartIdx = useStoreState((state) => state.drive.frameStartIdx);
  const driveInfo = useStoreState((state) => state.drive.driveInfo);
  const driveOsfInfo = useStoreState((state) => state.drive.driveOsfInfo);
  const playing = useStoreState((state) => state.drive.playing);
  const playbackSpeed = useStoreState((state) => state.drive.playbackSpeed);
  const playbackSpeedOptions = useStoreState(
    (state) => state.drive.playbackSpeedOptions
  );
  const isSample = useStoreState((state) => state.drive.isSample);
  const isPublished = useStoreState((state) => state.drive.isPublished);
  const sourceFileInfo = useStoreState((state) => state.drive.sourceFileInfo);
  const isShared = useStoreState((state) => state.drive.isShared);

  // fetch organizationId from driveInfo instead of searchParams
  const driveOrgId = Number(orgId) ?? 0;
  const filesToDownloadReady = useStoreState(
    (state) => state.drive.filesToDownloadReady
  );
  const outputProcesses = useStoreState((state) => state.drive.outputProcesses);

  const pointSize = useStoreState((state) => state.vizScene.pointSize);
  const leftSidebarExpanded = useStoreState(
    (state) => state.vizScene.leftSidebarExpanded
  );
  const flipped = useStoreState((state) => state.vizScene.flipped);
  const twoDImageSettings = useStoreState(
    (state) => state.vizScene.twoDImageSettings
  );
  const is2DImageVisible = useStoreState(
    (state) => state.vizScene.is2DImageVisible
  );
  const metadata = useStoreState((state) => state.vizScene.metadata);
  const dataMessage = useStoreState((state) => state.vizScene.dataMessage);
  const hasVizError = useStoreState((state) => state.vizScene.hasVizError);
  const vizErrorMessage = useStoreState(
    (state) => state.vizScene.vizErrorMessage
  );
  const availableChannels = useStoreState(
    (state) => state.vizScene.availableChannels
  );
  const rgbChannels = useStoreState((state) => state.vizScene.rgbChannels);
  const channelsResolved = useStoreState(
    (state) => state.vizScene.channelsResolved
  );

  const pointCloudGlobalSettings = useStoreState((state) =>
    selectPointCloudGlobalSettings(state.vizScene)
  );

  const pointCloudSensorsSettings = useStoreState((state) =>
    selectPointCloudSensorsSettings(state.vizScene)
  );

  const currentSensorImageSettings = useStoreState((state) =>
    selectSelectedSensorImageSettings(state.vizScene)
  );
  const selectedSensorAutoLevelsByImageIndex = useStoreState((state) => {
    const selectedSn = state.vizScene.twoDImageSettings.selectedImagePanelSn;
    if (!selectedSn) {
      return undefined;
    }

    return state.vizScene.lastUserImageAutoLevelsBySensorIdx[selectedSn];
  });

  const {
    incrementPointSize,
    decrementPointSize,
    togglePointCloudAutoLevel,
    toggleCameraOrthographicMode,
    toggle2DImageVisibility,
    setIs2DImageVisible,
    toggleAngleRulersVisibility,
    setLeftSidebarExpanded,
    setFlipped,
    updatePointCloudGlobalSettings,
    updatePointCloudSensorsSettings,
    updateDataMessage,
    togglePointCloudVisibility,
    updateMetadata,
    swapImages,
    addImage,
    deleteImage,
    updateImage,
    setHasVizError,
    setVizErrorMessage,
    setSelectedImagePanelSn,
    setShouldReplayCachedFrameOnRecreate,
  } = useStoreActions((actions) => actions.vizScene);

  const {
    setCurrentFrameIdx,
    setlastReceivedFrameIdx,
    loadDriveById,
    refreshDriveById,
    loadOutputFileInfo,
    loadSourceFileInfo,
    togglePause,
    setPlaybackSpeed,
    increasePlaybackSpeed,
    decreasePlaybackSpeed,
    setSeeking,
    setPlaying,
    setNumFrames,
    updateTags,
    setMappedTags,
  } = useStoreActions((actions) => actions.drive);
  // the imageSettings here represents all sensors image settings
  const { imageInfo, imageSettings, showImageRulers, selectedImagePanelSn } =
    twoDImageSettings;

  const tags = useStoreState((state) => state.recordingTag.tags);

  useEffect(() => {
    if (!isMobileViewport) {
      return;
    }

    if (is2DImageVisible) {
      setIs2DImageVisible(false);
    }

    setLeftSidebarExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileViewport, setIs2DImageVisible, setLeftSidebarExpanded]);

  // Sidebar expand/collapse is a layout transition, not a window resize.
  // Trigger one immediate refresh and one on transition end so pointcloud
  // redraw uses final container dimensions.
  useEffect(() => {
    const refreshLayout = () => {
      vizPageRef.current?.refreshPointCloudLayout();
    };

    requestAnimationFrame(refreshLayout);

    const sceneContainer = sceneContainerRef.current;
    if (!sceneContainer) {
      const fallbackTimer = setTimeout(
        refreshLayout,
        SIDEBAR_TRANSITION_FALLBACK_MS
      );
      return () => {
        clearTimeout(fallbackTimer);
      };
    }

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.target !== sceneContainer || event.propertyName !== 'margin') {
        return;
      }
      refreshLayout();
    };

    sceneContainer.addEventListener(
      'transitionend',
      handleTransitionEnd as EventListener
    );

    const fallbackTimer = setTimeout(
      refreshLayout,
      SIDEBAR_TRANSITION_FALLBACK_MS
    );

    return () => {
      sceneContainer.removeEventListener(
        'transitionend',
        handleTransitionEnd as EventListener
      );
      clearTimeout(fallbackTimer);
    };
  }, [leftSidebarExpanded]);

  const { fetchTags, createTag } = useStoreActions(
    (actions) => actions.recordingTag
  );

  const { curUserOrg, user, userOrgs } = useStoreState(
    (storeState) => storeState.user
  );
  const { fetchUserOrgs, setCurUserOrg } = useStoreActions(
    (actions) => actions.user
  );

  useEffect(() => {
    const getUserOrg = async () => {
      if (!user?.identity_id) return;
      try {
        await fetchUserOrgs(user.identity_id);
      } catch (error) {
        console.warn(error);
      }
    };
    getUserOrg();
  }, [driveOrgId, fetchUserOrgs, setCurUserOrg, user?.identity_id]);

  // TODO mw: Extract this logic to User store
  const isOusterUser = useMemo(() => {
    if (curUserOrg?.name === 'Ouster Inc') {
      return true;
    }
    return false;
  }, [curUserOrg]);

  // When the user changes the id in the URL, we don't want to have to
  // worry about resetting all the reducers state. The experience here
  // should be to start with a fresh page, so we just force a page
  // reload. If we wanted to make this process faster and not go through
  // login, we could try and be diligent about calling and tweaking
  // resetImmerState in all the appropriate reducers
  const previousDriveId = usePrevious(driveId);
  useEffect(() => {
    if (driveId && previousDriveId && driveId !== previousDriveId) {
      window.location.reload();
    }
  }, [driveId, previousDriveId]);
  const previousPublishedDriveUuid = usePrevious(publishedDriveUuid);

  useEffect(() => {
    if (
      publishedDriveUuid &&
      previousPublishedDriveUuid &&
      publishedDriveUuid !== previousPublishedDriveUuid
    ) {
      window.location.reload();
    }
  }, [publishedDriveUuid, previousPublishedDriveUuid]);

  // This starts loading a drive, and requests a list of source files.
  // Once the drive is loaded, state is updated and a rerender occurs
  // If the route was ../drive/..
  useEffect(() => {
    const loadDriveAndSource = async () => {
      try {
        if (driveId && !isUndefined(curUserOrg?.id)) {
          await loadDriveById({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
          await loadSourceFileInfo({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
        }
      } catch (error) {
        showToast({
          title: `Failed to download recording source file`,
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
      }
    };
    loadDriveAndSource();
  }, [
    driveId,
    loadDriveById,
    loadSourceFileInfo,
    driveOrgId,
    curUserOrg?.id,
    showToast,
  ]);

  // Fetch tags
  const disableTagRef = useRef(false);
  useEffect(() => {
    if (loadStatus !== 'success') return;

    if (userOrgs.some((org) => org.id === driveOrgId)) {
      if (!isShared) setCurUserOrg(driveOrgId);
      fetchTags(driveOrgId);
      disableTagRef.current = false;
    } else {
      disableTagRef.current = true;
    }
  }, [driveOrgId, fetchTags, setCurUserOrg, userOrgs, isShared, loadStatus]);

  // Tag objects
  const mappedTags: MappedTag[] = useMemo(() => mapData<Tag>(tags), [tags]);

  useEffect(() => {
    setMappedTags(mappedTags);
  }, [mappedTags, setMappedTags]);

  // keep the selected tags in the same order as the user selected
  // Map tag names in driveInfo to ids
  const mappedSelectedTags: MappedTag[] = useMemo(() => {
    if (!driveInfo) return [];
    const driveTags = driveInfo.tags;
    return mappedTags.filter((mappedTag) =>
      driveTags.includes(mappedTag.value)
    );
  }, [driveInfo, mappedTags]);

  const streamsInfo = useMemo<ReplayStreamInfo | undefined>(() => {
    if (!driveInfo || !metadata || isEmpty(metadata)) {
      return undefined;
    }

    const {
      dataCategory,
      uploadedBy,
      processedAt,
      startTime,
      sourceBucket,
      sourcePrefix,
      sensorInfos: driveSensorInfos,
    } = driveInfo;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const sensors: SensorSectionInfo[] = metadata.map(
      (singleSensorMetadata: any, index: number) => {
        const md = singleSensorMetadata ?? {};
        const mdSensorInfo = md.sensorInfo ?? {};
        const configParams = md.configParams ?? md.config_params ?? {};
        const dataFormat = md.dataFormat ?? md.format ?? {};

        const mdProductSn =
          mdSensorInfo.prodSn ?? md.productSn ?? md.sn ?? undefined;

        const driveSensorInfo =
          driveSensorInfos?.find(
            (dsi) => dsi.sdkProductSerialNumber === mdProductSn
          ) ??
          driveSensorInfos?.[index] ??
          {};

        const currentLidarMode =
          configParams.lidarMode ??
          configParams.lidar_mode ??
          (driveSensorInfo as any).sdkLidarMode ??
          md.lidarMode ??
          md.mode ??
          '';

        const currentUdpProfileLidar =
          configParams.udpProfileLidar ?? dataFormat.udpProfileLidar ?? '';
        const columnWindow =
          dataFormat.column_window ?? dataFormat.columnWindow;
        const columnsPerFrame =
          dataFormat.columns_per_frame ?? dataFormat.columnsPerFrame;
        const azimuthWindow = isValidAzimuthWindow(configParams.azimuthWindow)
          ? configParams.azimuthWindow
          : isValidAzimuthWindow(configParams.azimuth_window)
          ? configParams.azimuth_window
          : isValidAzimuthWindow(md.azimuthWindow)
          ? md.azimuthWindow
          : isValidAzimuthWindow(md.azimuth_window)
          ? md.azimuth_window
          : Array.isArray(columnWindow) && typeof columnsPerFrame === 'number'
          ? azimuthWindowFromColumnWindow(columnWindow, columnsPerFrame)
          : [0, 360000];

        const sensorConfig: SensorConfig = {
          lidarMode: currentLidarMode,
          udpProfileLidar: currentUdpProfileLidar,
          azimuthWindow,
          signalMultiplier:
            configParams.signalMultiplier ??
            configParams.signal_multiplier ??
            md.signalMultiplier ??
            md.signal_multiplier,
        };

        const si: SensorInfo = {
          sn:
            mdProductSn ??
            (driveSensorInfo as any).sdkProductSerialNumber ??
            `unknown-${index}`,
          prodLine:
            (driveSensorInfo as any).sdkProductType &&
            (driveSensorInfo as any).sdkProductResolution
              ? `${(driveSensorInfo as any).sdkProductType} - ${
                  (driveSensorInfo as any).sdkProductResolution
                }`
              : md.prod_line ?? md.prodLine ?? '',
          prodPn: (driveSensorInfo as any).sdkProductPartNumber ?? '',
          buildRev:
            (driveSensorInfo as any).sdkFwVersionCore ??
            (driveSensorInfo as any).sdkFwBuildRev ??
            md.buildRev ??
            md.fw_rev ??
            '',
          imageRev:
            (driveSensorInfo as any).sdkFwImageRev ??
            md.imageRev ??
            md.image_rev ??
            '',
          hwRev:
            (driveSensorInfo as any).sdkProductHwRevision ??
            md.hwRev ??
            md.hw_rev ??
            '',
          beamSpacing:
            (driveSensorInfo as any).sdkProductBeamSpacing ?? undefined,
        };

        return { info: si, config: sensorConfig };
      }
    );

    const filesInfo: FilesInfo = {
      files: sourceFileInfo.map((fileInfo) => {
        const { filename } = fileInfo;
        let name = filename;

        const recTitle = driveInfo?.recordingTitle;

        if (filename === 'extrinsic_parameters.json' || !recTitle) {
          return { name };
        }

        const lastDot = filename.lastIndexOf('.');
        const ext = lastDot !== -1 ? filename.substring(lastDot) : '';
        const base = lastDot !== -1 ? filename.substring(0, lastDot) : filename;

        if (ext.toLowerCase() === '.json') {
          const lastUnderscore = base.lastIndexOf('_');
          const suffix =
            lastUnderscore !== -1 ? base.substring(lastUnderscore + 1) : base;
          name = `${recTitle}_${suffix}${ext}`;
        } else {
          name = `${recTitle}${ext}`;
        }

        return { name };
      }),
      recordedOn: startTime === '...' ? undefined : startTime,
      uploadedOn: processedAt,
      uploadedBy: uploadedBy ?? undefined,
      dataCategory: dataCategory ?? undefined,
      sourceDir: isOusterUser
        ? `${GCS_BASE_URL}/${sourceBucket}/${sourcePrefix}`
        : undefined,
    };

    return {
      files: filesInfo,
      sensors,
    };
  }, [driveInfo, metadata, sourceFileInfo, isOusterUser]);

  const visibleProcessesInfo = useMemo(
    () =>
      isOusterUser
        ? outputProcesses
        : outputProcesses.map((process) => ({
            ...process,
            bucket: null,
            prefix: null,
          })),
    [isOusterUser, outputProcesses]
  );

  const pointCloudSettings = useMemo(
    () => ({
      global: pointCloudGlobalSettings,
      sensors: pointCloudSensorsSettings,
    }),
    [pointCloudGlobalSettings, pointCloudSensorsSettings]
  );

  // If the route was ../share/..
  useEffect(() => {
    if (publishedDriveUuid) {
      const loadPublishedDrive = async () => {
        try {
          await loadDriveById({
            id: publishedDriveUuid,
            isPrivate: false,
          });
          await loadSourceFileInfo({
            id: publishedDriveUuid,
            isPrivate: false,
          });
        } catch (error) {
          showToast({
            title: 'Failed to load published drive',
            description:
              error instanceof Error
                ? error.message
                : 'An unexpected error occurred',
            status: 'error',
          });
        }
      };
      loadPublishedDrive();
    }
  }, [publishedDriveUuid, loadDriveById, loadSourceFileInfo, showToast]);

  // Resolve lidar mode
  const lidarMode: string | undefined = useMemo(() => {
    const sdkLidarMode = driveInfo?.sensorInfos[0]?.sdkLidarMode;
    const primaryMetadata =
      Array.isArray(metadata) && metadata.length > 0 ? metadata[0] : metadata;
    if (!primaryMetadata) return sdkLidarMode ?? undefined;

    const { mode, config, configParams } = primaryMetadata as {
      mode?: string;
      config?: { lidar_mode?: string };
      configParams?: { lidarMode?: string; lidar_mode?: string };
    };
    const cpLidarMode =
      configParams?.lidarMode ?? configParams?.lidar_mode ?? config?.lidar_mode;
    return mode ?? cpLidarMode ?? sdkLidarMode ?? undefined;
  }, [driveInfo?.sensorInfos, metadata]);

  const dedupeSensorsBySn = useCallback(
    (sensorsMetadata: SDKSensorInfoAugmented[]) => {
      const deduped: SDKSensorInfoAugmented[] = [];
      const seen = new Set<string>();
      sensorsMetadata.forEach((sensorMeta, index) => {
        const rawSn =
          (sensorMeta as SDKSensorInfoAugmented & { productSn?: string })
            .productSn ?? sensorMeta.sn;
        const sn =
          typeof rawSn === 'string' && rawSn.length > 0
            ? rawSn
            : `unknown-${index}`;
        if (!seen.has(sn)) {
          seen.add(sn);
          deduped.push(sensorMeta);
        }
      });
      return deduped;
    },
    []
  );

  /// ///////////////////////////////////////////////////////////////////////////
  // Shortcuts
  /// ///////////////////////////////////////////////////////////////////////////
  const { shortcuts } = useStoreState((state) => ({ ...state.shortcut }));
  const { addShortcut } = useStoreActions((actions) => actions.shortcut);
  const { launchNewToast } = useStoreActions((actions) => actions.toast);
  // Keep currentFrameIdx inside a ref so we can use them in shortcuts
  // (set with useEffect) without updating the event listeners every
  // time
  const currentFrameIdxRef = useRef(0);
  useEffect(() => {
    currentFrameIdxRef.current = currentFrameIdx;
  }, [currentFrameIdx]);

  /// ///////////////////////////////////////////////////////////////////////////
  // Shortcuts Setup
  /// ///////////////////////////////////////////////////////////////////////////

  // --- 1. REFS (State Tracking) ---
  // Tracks where we want to be (ignores slow URL updates)
  const intendedFrameRef = useRef(currentFrameIdxRef.current);
  // Tracks when you last pressed a key (to block auto-play)
  const lastInputTimeRef = useRef(0);
  // Tracks direction: 1 (Forward), -1 (Backward), 0 (Idle)
  const scrollDirectionRef = useRef(0);
  // Tracks the last frame we successfully showed (for Worker filtering)
  const lastRenderedFrameRef = useRef(currentFrameIdxRef.current);
  const requestEpochRef = useRef(0);
  const lastDataFrameRef = useRef<number | null>(null);
  const consecutiveFramesWithoutDataRef = useRef(0);
  const lastUserInteractionTimeRef = useRef<number>(0);
  // Live ref for `playing` so debounced callbacks can check current state
  const playingRef = useRef(false);
  useEffect(() => {
    playingRef.current = playing;
  }, [playing]);

  const loadedLocalFramesRef = useRef<number[]>([]);
  useEffect(() => {
    loadedLocalFramesRef.current = loadedLocalFrames;
  }, [loadedLocalFrames]);

  const hasVizErrorRef = useRef(false);
  useEffect(() => {
    hasVizErrorRef.current = hasVizError;
  }, [hasVizError]);

  const isApplyingSharedCameraRef = useRef(false);
  const lastAppliedSceneParamRef = useRef<string | null>(null);
  const lastEmittedCameraStateRef = useRef<CameraShareState | null>(null);
  const lastEmittedPointCloudSceneStateRef = useRef<{
    pointSize: number;
    showRangeRings: boolean;
  } | null>(null);
  const lastEmittedLayoutSceneStateRef = useRef<{
    is2DImageVisible: boolean;
    leftSidebarExpanded: boolean;
  } | null>(null);
  const lastEmittedImagePanelSceneStateRef = useRef<{
    selectedImagePanelSn: string | undefined;
    selectedSensorImageSettings: NonNullable<
      SceneShareState['selectedSensorImageSettings']
    >;
  } | null>(null);

  const debouncedCameraSceneUrlUpdate = useMemo(
    () =>
      lodashDebounce((cameraState: CameraShareState) => {
        updateSceneInUrlSafe({ camera: cameraState });
      }, CAMERA_URL_UPDATE_DEBOUNCE_MS),
    []
  );

  useEffect(
    () => () => {
      debouncedCameraSceneUrlUpdate.cancel();
    },
    [debouncedCameraSceneUrlUpdate]
  );

  const debouncedPointCloudSceneUrlUpdate = useMemo(
    () =>
      lodashDebounce(
        (pointCloudState: { pointSize: number; showRangeRings: boolean }) => {
          updateSceneInUrlSafe(pointCloudState);
        },
        SCENE_SETTING_URL_UPDATE_DEBOUNCE_MS
      ),
    []
  );

  useEffect(
    () => () => {
      debouncedPointCloudSceneUrlUpdate.cancel();
    },
    [debouncedPointCloudSceneUrlUpdate]
  );

  const debouncedLayoutSceneUrlUpdate = useMemo(
    () =>
      lodashDebounce(
        (layoutState: {
          is2DImageVisible: boolean;
          leftSidebarExpanded: boolean;
        }) => {
          updateSceneInUrlSafe(layoutState);
        },
        SCENE_SETTING_URL_UPDATE_DEBOUNCE_MS
      ),
    []
  );

  useEffect(
    () => () => {
      debouncedLayoutSceneUrlUpdate.cancel();
    },
    [debouncedLayoutSceneUrlUpdate]
  );

  const debouncedImagePanelSceneUrlUpdate = useMemo(
    () =>
      lodashDebounce(
        (imagePanelState: {
          selectedImagePanelSn?: string;
          selectedSensorImageSettings: NonNullable<
            SceneShareState['selectedSensorImageSettings']
          >;
        }) => {
          updateSceneInUrlSafe(imagePanelState);
        },
        SCENE_SETTING_URL_UPDATE_DEBOUNCE_MS
      ),
    []
  );

  useEffect(
    () => () => {
      debouncedImagePanelSceneUrlUpdate.cancel();
    },
    [debouncedImagePanelSceneUrlUpdate]
  );

  const handleCameraShareStateChange = useCallback(
    (cameraState: CameraShareState) => {
      if (isApplyingSharedCameraRef.current) {
        return;
      }

      if (isEqual(lastEmittedCameraStateRef.current, cameraState)) {
        return;
      }

      lastEmittedCameraStateRef.current = cameraState;
      debouncedCameraSceneUrlUpdate(cameraState);
    },
    [debouncedCameraSceneUrlUpdate]
  );

  useEffect(() => {
    if (isApplyingSharedCameraRef.current) {
      return;
    }

    const nextPointCloudState = {
      pointSize,
      showRangeRings: pointCloudGlobalSettings.showRangeRings,
    };

    if (
      isEqual(lastEmittedPointCloudSceneStateRef.current, nextPointCloudState)
    ) {
      return;
    }

    lastEmittedPointCloudSceneStateRef.current = nextPointCloudState;
    debouncedPointCloudSceneUrlUpdate(nextPointCloudState);
  }, [
    debouncedPointCloudSceneUrlUpdate,
    pointSize,
    pointCloudGlobalSettings.showRangeRings,
  ]);

  useEffect(() => {
    if (isApplyingSharedCameraRef.current) {
      return;
    }

    const nextLayoutState = {
      is2DImageVisible,
      leftSidebarExpanded,
    };

    if (isEqual(lastEmittedLayoutSceneStateRef.current, nextLayoutState)) {
      return;
    }

    lastEmittedLayoutSceneStateRef.current = nextLayoutState;
    debouncedLayoutSceneUrlUpdate(nextLayoutState);
  }, [debouncedLayoutSceneUrlUpdate, is2DImageVisible, leftSidebarExpanded]);

  useEffect(() => {
    if (isApplyingSharedCameraRef.current) {
      return;
    }

    // Avoid persisting transient bootstrap/fallback image modes to URL
    // before channel availability (including RGB) has fully resolved.
    if (!channelsResolved) {
      return;
    }

    const nextImagePanelState = {
      selectedImagePanelSn: selectedImagePanelSn ?? undefined,
      selectedSensorImageSettings: toSharedImageSettings(
        currentSensorImageSettings,
        selectedSensorAutoLevelsByImageIndex
      ),
    };

    if (
      isEqual(lastEmittedImagePanelSceneStateRef.current, nextImagePanelState)
    ) {
      return;
    }

    lastEmittedImagePanelSceneStateRef.current = nextImagePanelState;
    debouncedImagePanelSceneUrlUpdate(nextImagePanelState);
  }, [
    channelsResolved,
    currentSensorImageSettings,
    debouncedImagePanelSceneUrlUpdate,
    selectedImagePanelSn,
    selectedSensorAutoLevelsByImageIndex,
  ]);

  useEffect(() => {
    if (!routeSceneState) {
      return;
    }

    const hasImagePanelState =
      routeSceneState.selectedImagePanelSn !== undefined ||
      (Array.isArray(routeSceneState.selectedSensorImageSettings) &&
        routeSceneState.selectedSensorImageSettings.length > 0);
    if (
      hasImagePanelState &&
      (!Array.isArray(metadata) || metadata.length === 0 || !channelsResolved)
    ) {
      return;
    }

    const sharedCameraState = routeSceneState.camera;
    const vizPage = vizPageRef.current;
    if (sharedCameraState && (!isVizInitialized || !vizPage)) {
      return;
    }

    if (lastAppliedSceneParamRef.current === routeSceneParam) {
      return;
    }

    lastAppliedSceneParamRef.current = routeSceneParam;
    isApplyingSharedCameraRef.current = true;

    if (sharedCameraState && vizPage) {
      const shouldUseOrthographic =
        sharedCameraState.cameraType === 'orthographic';
      if (
        pointCloudGlobalSettings.isOrthographicCamera !== shouldUseOrthographic
      ) {
        updatePointCloudGlobalSettings({
          isOrthographicCamera: shouldUseOrthographic,
        });
      }

      vizPage.applyCameraShareState(sharedCameraState);
      lastEmittedCameraStateRef.current = sharedCameraState;
    }

    if (routeSceneState.pointSize !== undefined) {
      updatePointCloudGlobalSettings({ pointSize: routeSceneState.pointSize });
    }

    if (routeSceneState.showRangeRings !== undefined) {
      updatePointCloudGlobalSettings({
        showRangeRings: routeSceneState.showRangeRings,
      });
    }

    if (routeSceneState.is2DImageVisible !== undefined) {
      setIs2DImageVisible(routeSceneState.is2DImageVisible);
    }

    if (routeSceneState.leftSidebarExpanded !== undefined) {
      setLeftSidebarExpanded(routeSceneState.leftSidebarExpanded);
    }

    if (routeSceneState.selectedImagePanelSn !== undefined) {
      setSelectedImagePanelSn(routeSceneState.selectedImagePanelSn);
    }

    if (
      routeSceneState.selectedImagePanelSn &&
      Array.isArray(routeSceneState.selectedSensorImageSettings) &&
      routeSceneState.selectedSensorImageSettings.length > 0
    ) {
      const sensorSn = routeSceneState.selectedImagePanelSn;
      const targetSettingsCount =
        routeSceneState.selectedSensorImageSettings.length;
      const currentSettingsCount = imageSettings[sensorSn]?.length ?? 0;

      for (
        let index = currentSettingsCount;
        index < targetSettingsCount;
        index += 1
      ) {
        addImage(sensorSn);
      }

      for (
        let index = currentSettingsCount - 1;
        index >= targetSettingsCount;
        index -= 1
      ) {
        deleteImage({ sensorSn, index });
      }

      routeSceneState.selectedSensorImageSettings.forEach((setting, index) => {
        const partialSettings = toPartialImageSettings(setting);
        updateImage({
          sensorSn,
          index,
          partialSettings,
          preserveExplicitToggles: true,
        });
      });
    }

    if (
      routeSceneState.pointSize !== undefined ||
      routeSceneState.showRangeRings !== undefined
    ) {
      lastEmittedPointCloudSceneStateRef.current = {
        pointSize: routeSceneState.pointSize ?? pointSize,
        showRangeRings:
          routeSceneState.showRangeRings ??
          pointCloudGlobalSettings.showRangeRings,
      };
    }

    if (
      routeSceneState.is2DImageVisible !== undefined ||
      routeSceneState.leftSidebarExpanded !== undefined
    ) {
      lastEmittedLayoutSceneStateRef.current = {
        is2DImageVisible: routeSceneState.is2DImageVisible ?? is2DImageVisible,
        leftSidebarExpanded:
          routeSceneState.leftSidebarExpanded ?? leftSidebarExpanded,
      };
    }

    if (
      routeSceneState.selectedImagePanelSn !== undefined ||
      (Array.isArray(routeSceneState.selectedSensorImageSettings) &&
        routeSceneState.selectedSensorImageSettings.length > 0)
    ) {
      lastEmittedImagePanelSceneStateRef.current = {
        selectedImagePanelSn:
          routeSceneState.selectedImagePanelSn ??
          selectedImagePanelSn ??
          undefined,
        selectedSensorImageSettings:
          routeSceneState.selectedSensorImageSettings ??
          toSharedImageSettings(
            currentSensorImageSettings,
            selectedSensorAutoLevelsByImageIndex
          ),
      };
    }

    window.setTimeout(() => {
      isApplyingSharedCameraRef.current = false;
    }, 0);
  }, [
    isVizInitialized,
    pointSize,
    pointCloudGlobalSettings.isOrthographicCamera,
    pointCloudGlobalSettings.showRangeRings,
    is2DImageVisible,
    leftSidebarExpanded,
    metadata,
    channelsResolved,
    routeSceneParam,
    routeSceneState,
    currentSensorImageSettings,
    selectedSensorAutoLevelsByImageIndex,
    imageSettings,
    setIs2DImageVisible,
    addImage,
    deleteImage,
    setSelectedImagePanelSn,
    updatePointCloudGlobalSettings,
    updateImage,
    setLeftSidebarExpanded,
    selectedImagePanelSn,
  ]);

  // This will trigger the URL update 500ms AFTER you stop pressing keys.
  const debouncedUrlUpdate = useMemo(
    () =>
      debounce((frame: number) => {
        updateFrameInUrl(frame);
      }, 500),
    []
  );

  useEffect(() => {
    if (playing) return;

    // GUARD: If the user pressed a key recently (< 500ms),
    // DO NOT update the URL here. Let the debounce handler take care of it.
    // This prevents the "every frame" history update while holding keys.
    if (Date.now() - lastInputTimeRef.current < 500) return;

    updateFrameInUrl(currentFrameIdx);
  }, [playing, currentFrameIdx]);

  useEffect(() => {
    setShouldReplayCachedFrameOnRecreate(!playing);
  }, [playing, setShouldReplayCachedFrameOnRecreate]);

  const handleTogglePause = useCallback(() => {
    // If we're transitioning into play mode, clear manual-step state
    // immediately so worker message filtering cannot drop initial forward frames.
    if (!playing) {
      scrollDirectionRef.current = 0;
      lastInputTimeRef.current = 0;
      intendedFrameRef.current = currentFrameIdxRef.current;
      // Keep playback responsive when resuming from pause without a seek.
      // Setting to current frame allows one step, then worker cadence gates progress.
      setlastReceivedFrameIdx(currentFrameIdxRef.current);
    }
    togglePause();
  }, [playing, setlastReceivedFrameIdx, togglePause]);

  const applyManualSeekDelta = useCallback(
    (delta: number) => {
      if (delta === 0) return;

      if (playingRef.current) {
        setPlaying(false);
      }

      const now = Date.now();

      // Reset if idle for >500ms (handles timeline clicks/jumps)
      if (now - lastInputTimeRef.current > 500) {
        intendedFrameRef.current = currentFrameIdxRef.current;
      }

      scrollDirectionRef.current = delta > 0 ? 1 : -1;
      const intendedCandidate = intendedFrameRef.current + delta;
      let nextIntended = intendedCandidate;

      // Backpressure: while paused manual stepping, don't allow intent to run
      // far ahead of what we have actually rendered.
      if (!playingRef.current) {
        const maxOutstandingFrames = 1;
        const lastRendered = lastRenderedFrameRef.current;
        if (delta > 0) {
          nextIntended = Math.min(
            intendedCandidate,
            lastRendered + maxOutstandingFrames
          );
        } else {
          nextIntended = Math.max(
            intendedCandidate,
            lastRendered - maxOutstandingFrames
          );
        }
      }

      intendedFrameRef.current = nextIntended;
      lastInputTimeRef.current = now;
      lastUserInteractionTimeRef.current = performance.now();

      if (intendedFrameRef.current !== currentFrameIdxRef.current) {
        setCurrentFrameIdx(intendedFrameRef.current);
      }

      debouncedUrlUpdate(intendedFrameRef.current);
    },
    [debouncedUrlUpdate, setCurrentFrameIdx, setPlaying]
  );

  useEffect(() => {
    // Throttled Handlers
    const handlePreviousFrame = throttle(() => applyManualSeekDelta(-1), 75);
    const handleNextFrame = throttle(() => applyManualSeekDelta(1), 75);
    const handleRewind10Frames = throttle(() => applyManualSeekDelta(-10), 75);
    const handleFastForward10Frames = throttle(
      () => applyManualSeekDelta(10),
      75
    );

    // Create all shortcuts with handlers
    const driveViewerShortcuts = createDriveViewerShortcuts({
      togglePause: handleTogglePause,
      increasePlaybackSpeed,
      decreasePlaybackSpeed,
      previousFrame: handlePreviousFrame,
      nextFrame: handleNextFrame,
      rewind10Frames: handleRewind10Frames,
      fastForward10Frames: handleFastForward10Frames,
      toggle2DImageVisibility,
      incrementPointSize: () => {
        const newSize = incrementPointSize();
        launchNewToast(`Point size: ${newSize}`);
      },
      decrementPointSize: () => {
        const newSize = decrementPointSize();
        launchNewToast(`Point size: ${newSize}`);
      },
      togglePointCloudAutoLevel: () => {
        try {
          const useLevels = togglePointCloudAutoLevel();
          launchNewToast(`Points auto-levels: ${useLevels ? 'ON' : 'OFF'}`);
        } catch (error) {
          if (error instanceof Error) {
            launchNewToast(`${error.message}`);
          } else {
            launchNewToast(`${error}`);
          }
        }
      },
      toggleCameraOrthographicMode: () => {
        const isOrthographic = toggleCameraOrthographicMode();
        launchNewToast(
          `Camera Type: ${isOrthographic ? 'Orthographic' : 'Perspective'}`
        );
      },
      toggleLeftSidebar: () => {
        setLeftSidebarExpanded(!leftSidebarExpanded);
      },
    });

    // Register all shortcuts
    driveViewerShortcuts.forEach((shortcut) => {
      addShortcut(shortcut);
    });

    return () => {
      handlePreviousFrame.cancel();
      handleNextFrame.cancel();
      handleRewind10Frames.cancel();
      handleFastForward10Frames.cancel();
    };
  }, [
    addShortcut,
    applyManualSeekDelta,
    decreasePlaybackSpeed,
    decrementPointSize,
    handleTogglePause,
    increasePlaybackSpeed,
    incrementPointSize,
    launchNewToast,
    toggle2DImageVisibility,
    toggleCameraOrthographicMode,
    togglePointCloudAutoLevel,
    setLeftSidebarExpanded,
    leftSidebarExpanded,
  ]);

  const [osfPath, setOsfPath] = useState<string | null>(null);
  const [osfType, setOsfType] = useState<OSFType | null>(null);
  // For binary OSF we shift currentFrameIdx from global frame (i.e.
  // across all bags/osf files) to the local frame index that starts at
  // the current file bag/OSF (the current 'section')
  const [currentLocalFrameIdx, setLocalCurrentFrameIdx] = useState<
    number | null
  >(null);

  // Current offset to the local section (aka current osf file playing)
  const [localOffset, setLocalOffset] = useState<number>(0);
  const feedbackFormLink = useFeedbackForm();

  const [driveSignedUrl, setDriveSignedUrl] = useState<string>();
  useEffect(() => {
    if (!osfPath) {
      return;
    }
    const osfDriveId = driveId ?? publishedDriveUuid;
    if (!osfDriveId) throw new Error(`Invalid osf drive id ${osfDriveId}`);

    const urlPromise = getSignedUrl(
      osfPath,
      osfDriveId,
      driveOrgId || curUserOrg?.id,
      !publishedDriveUuid
    );
    urlPromise.then((url) => {
      setDriveSignedUrl(url);
    });
  }, [driveId, osfPath, publishedDriveUuid, driveOrgId, curUserOrg?.id]);

  // Update OSF path and type for every change of frame
  useEffect(() => {
    if (driveOsfInfo) {
      const [sectionId, localFrameIdx] = getSectionIdFromGlobalFrame(
        currentFrameIdx,
        driveOsfInfo
      );
      setOsfPath(extractOSFPath(driveOsfInfo, sectionId));
      const newOSFType = extractOSFType(driveOsfInfo, sectionId);
      setOsfType(newOSFType);
      setLocalOffset(extractOSFOffset(driveOsfInfo, sectionId));
      if (newOSFType === OSFType.osf) {
        setLocalCurrentFrameIdx(localFrameIdx);
      }
    }
  }, [driveOsfInfo, currentFrameIdx]);

  // Initialize viz datasource
  useEffect(() => {
    updateDataMessage(new Subject<DataMessage[]>());
  }, [updateDataMessage]);

  /**
   * Refresh the drive info if any of the outputs are not finished
   */
  useEffect(() => {
    const loadFiles = async () => {
      try {
        if (driveId && !isUndefined(curUserOrg?.id)) {
          await loadOutputFileInfo({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
        } else if (publishedDriveUuid) {
          await loadOutputFileInfo({
            id: publishedDriveUuid,
            isPrivate: false,
          });
        }
      } catch (error) {
        showToast({
          title: `Failed to download recording outputs`,
          description:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred',
          status: 'error',
        });
      }
    };

    const allFinished =
      outputProcesses.length > 0 &&
      outputProcesses.every((o) =>
        [Status.Finished.toString(), Status.Failed.toString()].includes(
          o.generationStatus
        )
      );

    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    if (!allFinished) {
      // Keep polling while outputs are in progress. We schedule one refresh and
      // let this effect re-schedule on state updates until all processes finish.
      refreshTimer = setTimeout(() => {
        if (driveId && !isUndefined(curUserOrg?.id)) {
          refreshDriveById({
            id: driveId,
            isPrivate: true,
            orgId: driveOrgId || curUserOrg?.id,
          });
        } else if (publishedDriveUuid) {
          refreshDriveById({
            id: publishedDriveUuid,
            isPrivate: false,
          });
        }
      }, DRIVE_REFRESH_DELAY);
    } else if (
      allFinished &&
      filesToDownloadReady === false &&
      (driveId || publishedDriveUuid)
    ) {
      loadFiles();
    }

    return () => {
      if (refreshTimer) {
        clearTimeout(refreshTimer);
      }
    };
  }, [
    outputProcesses,
    driveId,
    publishedDriveUuid,
    filesToDownloadReady,
    loadOutputFileInfo,
    refreshDriveById,
    driveOrgId,
    curUserOrg?.id,
    showToast,
  ]);

  // ============================
  // BEGIN: Worker integrations
  // ============================

  // Same creation pattern as StreamedFileVizProvider
  const worker = useMemo(() => {
    const wk = getOSFWorker();
    return wk;
  }, []);

  // Open-once-per-URL sentinel
  const openedRef = useRef(false);

  // last interaction + skip threshold remain as-is
  const AUTO_SKIP_THRESHOLD = 500;
  const isDraggingSeekRef = useRef(false);
  const wasPlayingBeforeSeekRef = useRef(false);
  const seekSpinnerTimerRef = useRef<NodeJS.Timeout>();
  const pendingSeekTargetRef = useRef<number | null>(null);
  const [showActiveScrubSpinner, setShowActiveScrubSpinner] = useState(false);
  const [showSeekTransitionSpinner, setShowSeekTransitionSpinner] =
    useState(false);

  const clearSeekTransitionSpinner = useCallback(() => {
    if (seekSpinnerTimerRef.current) {
      clearTimeout(seekSpinnerTimerRef.current);
      seekSpinnerTimerRef.current = undefined;
    }
    pendingSeekTargetRef.current = null;
    setShowSeekTransitionSpinner(false);
  }, []);

  const startSeekTransitionSpinner = useCallback(
    (targetFrame: number, shouldShow: boolean) => {
      if (seekSpinnerTimerRef.current) {
        clearTimeout(seekSpinnerTimerRef.current);
        seekSpinnerTimerRef.current = undefined;
      }
      setShowSeekTransitionSpinner(false);
      pendingSeekTargetRef.current = targetFrame;

      if (!shouldShow) {
        return;
      }

      seekSpinnerTimerRef.current = setTimeout(() => {
        if (pendingSeekTargetRef.current === targetFrame) {
          setShowSeekTransitionSpinner(true);
        }
      }, SEEK_TRANSITION_SPINNER_DELAY_MS);
    },
    []
  );

  // Terminate on unmount (unchanged from your version, just grouped here)
  useEffect(
    () =>
      // Cleanup function to terminate the worker on component unmount
      () => {
        clearSeekTransitionSpinner();
        worker.terminate();
      },
    [clearSeekTransitionSpinner, worker]
  );

  const metadataInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!worker) {
      return () => {};
    }

    const handleWorkerMessage = (e: MessageEvent<WorkerMessage>) => {
      const { data } = e;
      if (!data) return;

      if ('command' in data && data.command === 'error') {
        const message =
          typeof data.message === 'string'
            ? data.message
            : 'We have an issue to construct the Osf file';
        setHasVizError(true);
        setVizErrorMessage(message);

        return;
      }

      if ('command' in data && data.command === 'init') {
        if (!metadataInitializedRef.current) {
          const rawInfos =
            Array.isArray(data.allSensorsInfo) && data.allSensorsInfo.length > 0
              ? data.allSensorsInfo
              : [data.sensorInfo ?? []].flat();

          const normalized = rawInfos
            .map((s) => normalizeSensorInfoFromWorker(s, driveInfo))
            .filter(Boolean);
          const deduped = dedupeSensorsBySn(normalized);

          if (deduped.length > 0) {
            updateMetadata(deduped);
            metadataInitializedRef.current = true;
          }
        }
        return;
      }

      if ('framesCount' in data) {
        const { framesCount } = data as { framesCount: number };
        if (Number.isFinite(framesCount) && framesCount > 0) {
          setNumFrames(framesCount);
        }
      }

      if ('calculatedFps' in e.data) {
        const { calculatedFps } = e.data as { calculatedFps: number };
        if (Number.isFinite(calculatedFps)) {
          setWorkerFps(calculatedFps);
        }
        return;
      }

      if (Object.hasOwn(e.data, 'loadedFrames')) {
        const msg = e.data as { loadedFrames: number[] };
        if (!isEqual(msg.loadedFrames, loadedLocalFramesRef.current)) {
          setLoadedLocalFrames(msg.loadedFrames);
        }
        return;
      }

      if (Object.hasOwn(e.data, 'frameIdx')) {
        const msg = e.data as {
          frameIdx: number;
          dataMessages?: DataMessage[];
          ts?: number;
          reqEpoch?: number;
        };

        if (
          typeof msg.reqEpoch === 'number' &&
          msg.reqEpoch < requestEpochRef.current
        ) {
          return;
        }

        // Prevents "Old" frames from overwriting "New" frames during lag
        const direction = scrollDirectionRef.current;
        const lastRendered = lastRenderedFrameRef.current;
        const targetFrame = currentFrameIdxRef.current;
        const isPlaying = playingRef.current;

        // Calculate if the keyboard was used recently (within 500ms).
        const isKeyboardActive = Date.now() - lastInputTimeRef.current < 500;
        const isManualSeekSettling =
          !isPlaying && direction !== 0 && lastRendered !== targetFrame;

        // While paused and the keyboard is active, ignore stale worker frames
        // that are far from the current target to avoid one-frame flash jumps.
        if (
          !isPlaying &&
          isKeyboardActive &&
          direction === 0 &&
          Math.abs(msg.frameIdx - targetFrame) > KEYBOARD_SEEK_TARGET_TOLERANCE
        ) {
          return;
        }

        // ONLY apply the filter if the keyboard is actively being used.
        // If the user clicked the Playbar or hit Play, 'isKeyboardActive' will be false,
        // so we skip this block and allow the frame to load immediately.
        if (isManualSeekSettling) {
          // Guard against stale in-flight frames that are beyond the desired target.
          // This prevents delayed "auto-play" after key release.
          if (direction === -1 && msg.frameIdx > targetFrame + 1) {
            return;
          }

          if (direction === 1 && msg.frameIdx < targetFrame - 1) {
            return;
          }

          // Backward Filter
          if (direction === -1 && msg.frameIdx > lastRendered) {
            return;
          }

          // Forward Filter
          if (direction === 1 && msg.frameIdx < lastRendered) {
            return;
          }
        }

        // If accepted, update the tracker
        lastRenderedFrameRef.current = msg.frameIdx;

        // Manual seek has converged to target; clear direction state so
        // subsequent non-keyboard frames are not filtered by old direction.
        if (!isPlaying && direction !== 0 && msg.frameIdx === targetFrame) {
          scrollDirectionRef.current = 0;
        }
        // ===================================

        setlastReceivedFrameIdx(msg.frameIdx);

        const hasFrameData =
          Array.isArray(msg.dataMessages) && msg.dataMessages.length > 0;

        // Clear transition spinner on the first accepted frame after seek.
        // During resume-to-play, currentFrameIdx can advance before the exact
        // seek target response lands, so equality-only checks can leave the
        // spinner stuck indefinitely.
        if (pendingSeekTargetRef.current !== null) {
          clearSeekTransitionSpinner();
        }

        if (msg.ts) setFrameTs(msg.ts);

        if (hasFrameData) {
          const frameData = msg.dataMessages as DataMessage[];

          consecutiveFramesWithoutDataRef.current = 0;
          lastDataFrameRef.current = msg.frameIdx;

          // Cache the data for potential replay when settings change
          frameCache.lastDataMessage = frameData;

          dataMessage.next(frameData);
        } else {
          consecutiveFramesWithoutDataRef.current += 1;

          if (
            !hasVizErrorRef.current &&
            // Ensure this check uses the same ref
            performance.now() - lastUserInteractionTimeRef.current >
              AUTO_SKIP_THRESHOLD
          ) {
            clearSeekTransitionSpinner();
            updateFrameInUrl(msg.frameIdx + 1);
          }
        }
      }

      // Handle URL renewal request from worker
      if ('command' in e.data && e.data.command === 'renewUrl') {
        const renewUrl = async () => {
          if (!osfPath) {
            console.warn('Cannot renew URL: OSF path invalid');
            return;
          }
          const osfDriveId = driveId ?? publishedDriveUuid;
          if (!osfDriveId) {
            console.warn('Cannot renew URL: Invalid drive ID');
            return;
          }

          try {
            const newUrl = await getSignedUrl(
              osfPath,
              osfDriveId,
              driveOrgId || curUserOrg?.id,
              !publishedDriveUuid
            );

            // Update state and send new URL to worker
            setDriveSignedUrl(newUrl);
            worker.postMessage({
              command: 'renewUrl',
              signedOsfUrl: newUrl,
            });
          } catch (error) {
            console.error('Failed to renew signed URL:', error);
            worker.postMessage({
              command: 'renewUrlFailed',
              error: error instanceof Error ? error.message : 'Unknown error',
            });
            showToast({
              title: 'Failed to renew access',
              description:
                'Unable to refresh the signed URL. Please reload the page.',
              status: 'error',
            });
          }
        };

        renewUrl();
      }
    };

    worker.addEventListener('message', handleWorkerMessage);

    return () => {
      worker.removeEventListener('message', handleWorkerMessage);
    };
  }, [
    curUserOrg?.id,
    dataMessage,
    driveId,
    driveInfo,
    driveOrgId,
    osfPath,
    publishedDriveUuid,
    setHasVizError,
    setNumFrames,
    setVizErrorMessage,
    setlastReceivedFrameIdx,
    showToast,
    dedupeSensorsBySn,
    clearSeekTransitionSpinner,
    updateMetadata,
    worker,
  ]);

  // Open the OSF exactly once per new URL, then issue a 'get' for the current frame
  useEffect(() => {
    if (!window.Worker || !driveSignedUrl) return;

    const recordingId = driveId || publishedDriveUuid;
    if (!openedRef.current) {
      // Use the detection function directly rather than the hook state,
      // which may still be false on the very first render cycle.
      const mobile = isMobileDevice();
      worker.postMessage({
        command: 'open',
        signedOsfUrl: driveSignedUrl,
        bufferSize: mobile ? 30 : 360,
        rate: 1,
        recordingId,
        isMobile: mobile,
      });
      openedRef.current = true;
    }

    // We can only render VizPage (and set isVizInitialized) once that metadata exists.
    // Therefore, keep `open` unconditional and gate only frame fetches on viz readiness.
    if (!isVizInitialized) {
      return;
    }

    const isManualSeekActive =
      Date.now() - lastInputTimeRef.current < MANUAL_SEEK_ACTIVE_WINDOW_MS;
    // On mobile, suppress prefetching entirely when paused to avoid memory
    // pressure that causes the browser to kill the tab.
    const mobilePausedNoPrefetch = isMobileViewport && !playingRef.current;
    const shouldPreload =
      !seeking && !isManualSeekActive && !mobilePausedNoPrefetch;

    worker.postMessage({
      command: 'get',
      index: currentFrameIdx,
      preload: shouldPreload,
      reqEpoch: requestEpochRef.current,
    });
  }, [
    currentFrameIdx,
    seeking,
    driveSignedUrl,
    isVizInitialized,
    worker,
    driveId,
    publishedDriveUuid,
    isMobileViewport,
  ]);

  const requestCurrentFrameWhenPaused = useMemo(
    () =>
      debounce((index: number) => {
        // Guard: if playback resumed since this debounce was scheduled,
        // skip the stale preload=false request — it would arrive at the
        // worker after the animation loop has advanced and trigger a
        // false backward-jump reset.
        if (playingRef.current) return;

        // During active manual stepping (keyboard/playbar hold), avoid
        // emitting extra paused repaint requests that race with main gets.
        if (
          Date.now() - lastInputTimeRef.current <
          MANUAL_SEEK_ACTIVE_WINDOW_MS
        ) {
          return;
        }

        worker.postMessage({
          command: 'get',
          index,
          preload: false,
          reqEpoch: requestEpochRef.current,
        });
      }, 50),
    [worker]
  );

  useEffect(() => {
    if (!playing) {
      return;
    }

    // Clear stale manual-step state so play mode does not inherit
    // backward/forward keyboard filtering decisions.
    scrollDirectionRef.current = 0;
    lastInputTimeRef.current = 0;
    intendedFrameRef.current = currentFrameIdxRef.current;
  }, [playing]);

  // While paused, re-request the current frame if visualization settings
  // change so rebuilt pipelines repaint without impacting playback throughput.
  useEffect(() => {
    if (
      !window.Worker ||
      !driveSignedUrl ||
      !openedRef.current ||
      !isVizInitialized ||
      playing
    ) {
      return;
    }

    if (Date.now() - lastInputTimeRef.current < MANUAL_SEEK_ACTIVE_WINDOW_MS) {
      return;
    }

    requestCurrentFrameWhenPaused(currentFrameIdx);
  }, [
    availableChannels,
    currentFrameIdx,
    currentSensorImageSettings,
    driveSignedUrl,
    imageSettings,
    is2DImageVisible,
    isVizInitialized,
    playing,
    pointCloudGlobalSettings,
    pointCloudSensorsSettings,
    requestCurrentFrameWhenPaused,
  ]);

  // When URL changes, allow re-open
  useEffect(() => {
    openedRef.current = false;
    metadataInitializedRef.current = false;
  }, [driveSignedUrl]);

  // Poll for loaded frames 4x/s (unchanged)
  useAnimationFrameThrottled(() => {
    if (Date.now() - lastInputTimeRef.current < MANUAL_SEEK_ACTIVE_WINDOW_MS) {
      return;
    }

    worker.postMessage({
      requestLoadedFrames: true,
    });
  }, 4);

  // ============================
  // END: Worker integrations
  // ============================
  const lidarModeFrequency = useMemo(
    () => (lidarMode ? frequencyFromLidarMode(lidarMode) : 10),
    [lidarMode]
  );
  const effectiveReplayFPS = useMemo(
    () =>
      shouldUseWorkerFps(replayFPS, lidarModeFrequency)
        ? replayFPS
        : lidarModeFrequency,
    [replayFPS, lidarModeFrequency]
  );
  const playbarTimestampMicros = useMemo(() => {
    if (
      driveInfo?.startTime instanceof Date &&
      Number.isFinite(effectiveReplayFPS) &&
      effectiveReplayFPS > 0
    ) {
      const relativeFrameIdx = Math.max(0, currentFrameIdx - frameStartIdx);
      return (
        driveInfo.startTime.getTime() * 1000 +
        Math.round((relativeFrameIdx * 1e6) / effectiveReplayFPS)
      );
    }

    return frameTs;
  }, [
    driveInfo?.startTime,
    effectiveReplayFPS,
    currentFrameIdx,
    frameStartIdx,
    frameTs,
  ]);
  /* 
    Due to the heavy work on rendering, replayFPS is an ideal rendering rate,
    but that is hard to achieve as is, it is impacted by the number of 2D image
    as well as the lidar mode. The higher resolution, and more 2D images will dramatically slow
    down the drawing speed. Hence, we have to apply some multiple to increase the drawing rate
    to match the desirable rate we want to have.
    However, it is still depended heavily on the hardware.
  */
  const adjustFrameRate = useMemo(
    () =>
      calculateAdjustedFrameRate(
        effectiveReplayFPS,
        currentSensorImageSettings.length,
        playbackSpeed
      ),
    [effectiveReplayFPS, currentSensorImageSettings.length, playbackSpeed]
  );

  const maxLoadedGlobalIdx = useMemo(() => {
    if (currentLocalFrameIdx === null) {
      return localOffset;
    }

    return (
      getMaxLoadedLocalFrame(currentLocalFrameIdx, loadedLocalFrames) +
      localOffset
    );
  }, [currentLocalFrameIdx, loadedLocalFrames, localOffset]);

  useAnimationFrameThrottled(() => {
    // GUARD: If user pressed a key recently (<500ms), STOP auto-play immediately.
    if (Date.now() - lastInputTimeRef.current < 500) {
      return;
    }

    // If the empty-frame auto-skip logic accidentally pushes the frame index out of bounds
    // at the end of the file, this catches it and safely loops it back to 0, preventing the deadlock.
    if (numFrames > 1 && currentFrameIdx >= numFrames) {
      setCurrentFrameIdx(0);
      setlastReceivedFrameIdx(-1);
      return;
    }

    const nextFrame =
      numFrames <= 1 ? currentFrameIdx : mod(currentFrameIdx + 1, numFrames);

    if (Number.isNaN(nextFrame)) return;

    // We removed the `< 5` check so playback won't freeze when the worker gets far ahead.
    const canAdvanceNormally =
      typeof lastReceivedFrameIdx === 'number' &&
      lastReceivedFrameIdx >= currentFrameIdx;

    if (playing && canAdvanceNormally) {
      // FIX 3: Catch loops/wraparounds (e.g., 99 -> 0).
      // By resetting `lastReceivedFrameIdx` to -1 here, we force the animation loop
      // to wait for the worker to catch up to the beginning of the file, completely
      // eliminating the fast-forwarding race condition.
      if (nextFrame < currentFrameIdx) {
        setlastReceivedFrameIdx(-1);
      }

      setCurrentFrameIdx(nextFrame);
    }
  }, adjustFrameRate);

  const deleteImageHandler = useCallback(
    (sensorSn: string, index: number) => {
      deleteImage({ sensorSn, index });
    },
    [deleteImage]
  );

  const updateImageHandler = useCallback(
    (
      sensorSn: string,
      index: number,
      partialSettings: Partial<ImageSettings>
    ) => {
      updateImage({ sensorSn, index, partialSettings });
    },
    [updateImage]
  );

  const onCreateTag = useCallback(
    async (name: string): Promise<Tag | undefined> => {
      if (!curUserOrg?.id) return undefined;

      try {
        const newTag = await createTag({
          organization_id: driveOrgId || curUserOrg.id,
          name,
        });
        return newTag as Tag;
      } catch (error) {
        showToast({
          title: 'Failed to add tag',
          description: `${error}`,
          status: 'error',
        });
        return undefined;
      }
    },
    [createTag, driveOrgId, curUserOrg?.id, showToast]
  );

  const handleTagsChange = useCallback(
    async (items: readonly (CreatedOption | MappedTag)[]) => {
      if (!curUserOrg?.id) return;
      if (items.length === 0) {
        updateTags({ newTags: [], orgId: driveOrgId || curUserOrg.id });
        return;
      }
      const tempTags: MappedTag[] = [];
      // eslint-disable-next-line no-restricted-syntax
      for await (const tag of items) {
        if ('__isNew__' in tag) {
          // newly created tag, check whether it exists
          const existingTag = mappedTags.find(
            (t) => t.data.name === formatTag(tag.value)
          );
          if (existingTag) {
            tempTags.push(existingTag);
          } else {
            // If tag doesn't exist, create a new tag and add it
            // TODO (mw): This await causes a slight delay when creating a new tag
            const newTag = await onCreateTag(formatTag(tag.value));
            if (newTag) {
              // successful create
              // make equivalent mapped tag (name should already be formatted)
              // to add to selected tags
              const mappedNewTag = {
                label: newTag.name,
                value: newTag.name,
                data: newTag,
              };
              tempTags.push(mappedNewTag);
            }
          }
        } else {
          tempTags.push(tag);
        }
      }
      updateTags({
        newTags: uniq(tempTags),
        orgId: driveOrgId || curUserOrg.id,
      });
    },
    [driveOrgId, curUserOrg?.id, updateTags, mappedTags, onCreateTag]
  );

  /* 
    below are the function passed to left side panel
    wrap them in useCallback to avoid unnecessary render of panel during parent rendering.
    If we don't memo the side panel, it keeps redrawing itself during the play,
    which significantly affect the performance
  */
  const toggleFlipped = useCallback(() => {
    setFlipped(!flipped);
  }, [flipped, setFlipped]);

  const onRetract = useCallback(() => {
    setLeftSidebarExpanded(false);
  }, [setLeftSidebarExpanded]);

  const swapImagesHandler = useCallback(
    (sensorSn: string, indexA: number, indexB: number) => {
      swapImages({ sensorSn, indexA, indexB });
    },
    [swapImages]
  );

  const updatePointCloudSensorSettingsHandler = useCallback(
    (sensorName: string, newSettings: Partial<PointCloudSensorSettings>) => {
      updatePointCloudSensorsSettings([sensorName, newSettings]);
    },
    [updatePointCloudSensorsSettings]
  );

  const handleSliderChange = useCallback(
    (values: ReadonlyArray<number>) => {
      if (!isDraggingSeekRef.current) {
        clearSeekTransitionSpinner();
        isDraggingSeekRef.current = true;
        setShowActiveScrubSpinner(true);
        wasPlayingBeforeSeekRef.current = playing;
        if (playing) {
          setPlaying(false);
        }
      }

      const sliderValue = Math.floor(values[0]);
      if (Number.isNaN(sliderValue)) return;

      lastUserInteractionTimeRef.current = performance.now();
      requestEpochRef.current += 1;
      if (!seeking) setSeeking(true);
      onSliderChange(values);
    },
    [clearSeekTransitionSpinner, playing, seeking, setPlaying, setSeeking]
  );

  const handleSliderMouseUp = useCallback(
    (values: ReadonlyArray<number>) => {
      const sliderValue = Math.floor(values[0]);
      lastUserInteractionTimeRef.current = performance.now();
      onSliderChange.cancel(); // Cancel trailing seek calls
      setSeeking(false);
      if (!Number.isNaN(sliderValue)) {
        requestEpochRef.current += 1;
        const sliderValueLocal = sliderValue - localOffset;
        const isLocalFrameCached = loadedLocalFrames.includes(sliderValueLocal);
        const shouldShowTransitionSpinner =
          wasPlayingBeforeSeekRef.current && !isLocalFrameCached;
        startSeekTransitionSpinner(sliderValue, shouldShowTransitionSpinner);

        setCurrentFrameIdx(sliderValue);
        updateFrameInUrl(sliderValue);
      } else {
        clearSeekTransitionSpinner();
      }
      isDraggingSeekRef.current = false;
      setShowActiveScrubSpinner(false);
      // On seek boundaries, gate playback until worker resolves the new target.
      if (!Number.isNaN(sliderValue)) {
        setlastReceivedFrameIdx(sliderValue - 1);
      }
      if (wasPlayingBeforeSeekRef.current) {
        setPlaying(true);
      }
      wasPlayingBeforeSeekRef.current = false;
    },
    [
      clearSeekTransitionSpinner,
      loadedLocalFrames,
      localOffset,
      setCurrentFrameIdx,
      setlastReceivedFrameIdx,
      setPlaying,
      setSeeking,
      startSeekTransitionSpinner,
    ]
  );

  const handlePlaybarStep = useCallback(
    (index: number) => {
      if (Number.isNaN(index)) return;

      const { current } = currentFrameIdxRef;
      const delta = index - current;

      applyManualSeekDelta(delta);
    },
    [applyManualSeekDelta]
  );

  const [isVizAlertShown, setIsVizAlertShown] = useState(false);
  const [hasVizAlert, setHasVizAlert] = useState(false);
  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();
  useEffect(() => {
    if (hasVizAlert && !isVizAlertShown) {
      onDialogOpen();
    }
  }, [hasVizAlert, isVizAlertShown, onDialogOpen]);
  const handleModalClose = () => {
    onDialogClose();
    setIsVizAlertShown(true);
  };
  useEffect(() => {
    if (driveInfo?.visualization_status === 'NOT_VIEWABLE' && !hasVizAlert) {
      setHasVizAlert(true);
    }
  }, [driveInfo?.visualization_status, hasVizAlert]);

  // This portion of code ensures the state of the frame index in the
  // reducer is in sync with the URL. Making the URL the 'source of
  // truth' simplifies things here and lets the user change the frame
  // directly by updating the URL. This piece of code should be the only
  // one calling setCurrentFrameIdx
  const prevRouteFrameIdx = usePrevious(routeFrameIdx);
  useEffect(() => {
    if (routeFrameIdx !== undefined && prevRouteFrameIdx !== routeFrameIdx) {
      setCurrentFrameIdx(routeFrameIdx);
    }
  }, [routeFrameIdx, prevRouteFrameIdx, setCurrentFrameIdx]);

  // Track when OSF processing failed
  useSentryErrorTracker(
    driveInfo?.osfGenerationStatus === Status.Failed,
    'OSF generation failed during data processing',
    'DriveViewer (OSF Failed)',
    { driveId, publishedDriveUuid }
  );

  // Track when we have a legacy OSF that cannot be visualized
  useSentryErrorTracker(
    osfType === OSFType.legacyOsf,
    'Data was processed into a legacy OSF format that can no longer be visualized',
    'DriveViewer (Legacy OSF)',
    { driveId, publishedDriveUuid }
  );

  // Track when we have a viz error
  useSentryErrorTracker(
    hasVizError,
    vizErrorMessage || 'Unknown visualization error',
    'DriveViewer (Viz Error)',
    { driveId, publishedDriveUuid }
  );

  useSentryErrorTracker(
    loadStatus === 'failed' || !driveInfo,
    'Drive load failed: Missing drive information or load error',
    'DriveViewer (Drive Load Error)',
    { driveId, publishedDriveUuid }
  );

  const isVizReady = vizPageRef.current?.getIsDrawn
    ? vizPageRef.current.getIsDrawn()
    : false;

  const leftSidebarElement = useMemo(() => {
    if (!streamsInfo) {
      return null;
    }

    return (
      <VizLeftSidebar
        pointCloudSettings={pointCloudSettings}
        availableChannels={availableChannels}
        show2DImage={is2DImageVisible}
        updatePointCloudGlobalSettings={updatePointCloudGlobalSettings}
        updatePointCloudSensorSettings={updatePointCloudSensorSettingsHandler}
        streamsInfo={streamsInfo}
        processesInfo={visibleProcessesInfo}
        on2DImageVisibilityChange={toggle2DImageVisibility}
        onPointCloudVisibilityChange={togglePointCloudVisibility}
        flexShrink={0}
        ref={leftSidebarRef}
        onRetract={onRetract}
        imageInfo={imageInfo}
        imageSettings={imageSettings}
        updateImage={updateImageHandler}
        deleteImage={deleteImageHandler}
        addImage={addImage}
        swapImages={swapImagesHandler}
        w="24rem"
        tags={
          publishedDriveUuid || disableTagRef.current
            ? undefined
            : mappedSelectedTags
        }
        allTagOptions={
          publishedDriveUuid || disableTagRef.current ? undefined : mappedTags
        }
        onTagsChange={handleTagsChange}
        formatTag={formatTag}
        isTagDisabled={isSample && !isAdminLevelRole(curUserOrg?.role)}
        flipped={flipped}
        toggleFlipped={toggleFlipped}
        showAngleRulers={showImageRulers}
        toggleAngleRulers={toggleAngleRulersVisibility}
        maxStackedImages={3}
        isOuster={isOusterUser}
        selectedImageSensorSn={selectedImagePanelSn ?? ''}
        on2DImageSensorChange={setSelectedImagePanelSn}
        hoverMode={hoverMode}
        setHoverMode={setHoverMode}
        paused={!playing}
        isVizReady={isVizReady}
        rgbChannels={rgbChannels}
      />
    );
  }, [
    streamsInfo,
    availableChannels,
    pointCloudSettings,
    is2DImageVisible,
    updatePointCloudGlobalSettings,
    updatePointCloudSensorSettingsHandler,
    visibleProcessesInfo,
    toggle2DImageVisibility,
    togglePointCloudVisibility,
    onRetract,
    imageInfo,
    imageSettings,
    updateImageHandler,
    deleteImageHandler,
    addImage,
    swapImagesHandler,
    publishedDriveUuid,
    mappedSelectedTags,
    mappedTags,
    handleTagsChange,
    isSample,
    curUserOrg?.role,
    flipped,
    toggleFlipped,
    showImageRulers,
    toggleAngleRulersVisibility,
    isOusterUser,
    selectedImagePanelSn,
    setSelectedImagePanelSn,
    hoverMode,
    setHoverMode,
    playing,
    isVizReady,
    rgbChannels,
  ]);

  const getGeneralErrorMessage = () => {
    if (isPublished || publishedDriveUuid) {
      // if this a published drive, user can't see it means the drive is deleted or unpublished
      return (
        <Box lineHeight="2rem" data-testid="published-drive-not-found">
          Unable to load the dataset. This may be due to the resource being
          deleted or corrupted. Please contact the person who shared the link
          with you.
        </Box>
      );
    }

    const hasViewingPermission = userOrgs.some((org) => org.id === driveOrgId);

    if (!hasViewingPermission) {
      // when user logged in but not a member of the org, they will see this error message
      // Fyi, for supported ouster user, they will be able to load the driveInfo, so they will not see this error message
      return (
        <Box lineHeight="2rem" data-testid="not-org-member-error">
          You are not a member of the organization this recording belongs to.
          Please ask that organization to invite you to see this recording.
        </Box>
      );
    }

    // when it is not related to the permission, user supposed to see the recording
    // but for some reason, we can't load the driveInfo, then we show this error message
    return (
      <Box data-testid="general-error-message" display="grid" rowGap="2rem">
        <Box lineHeight="2rem">
          Unable to load the dataset. This may be due to the resource being
          deleted or corrupted. Please go back to the list page and try again.
        </Box>
        <Box textAlign="center">
          <Button
            as={Link}
            to="/drive/private"
            variant="solid"
            data-testid="return-to-list-button"
          >
            Return to List page
          </Button>
        </Box>
        <Box>
          If you continue to experience issues, please reach out to us via this
          form:
        </Box>
        <Box textAlign="center">
          <Button
            as={Link}
            to={feedbackFormLink}
            variant="outline"
            data-testid="feedback-form-button"
          >
            Feedback Form
          </Button>
        </Box>
      </Box>
    );
  };

  if (loadStatus === 'loading' || loadStatus === 'notStarted') {
    return (
      /* 
        Use the consistent loading animation
      */
      <LoaderContainer>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </LoaderContainer>
    );
  }

  if (loadStatus === 'failed' || !driveInfo) {
    return (
      <Box
        m="auto"
        fontSize={18}
        display="grid"
        rowGap="2rem"
        maxWidth="60ch"
        textAlign="center"
        data-testid="general-error-container"
      >
        {getGeneralErrorMessage()}
      </Box>
    );
  }

  /* 
    Handle general viz error from ouster lib like viz, data source and osf
    the message from these lib will be print out and keep the nav bar so user can still download recording file
  */
  if (hasVizError) {
    return (
      <>
        <DriveNavBar orgId={driveOrgId} />
        <ContainerBelowNav>
          <VizInfoMessageContainer>
            <div data-testid="viz-error-container">
              We encountered an issue while trying to visualize your recording.
              The error message is as follows:{' '}
              <em id="errorMessageViz">{vizErrorMessage}</em>
              {'\n'}
              {'\n'}
              To investigate further, you can download the recording through the
              download button located on the top right. If needed, you can also
              reach out to the Ouster Studio Web team for assistance by filling
              out{' '}
              <FeedbackFormContainer formLink={feedbackFormLink}>
                this form
              </FeedbackFormContainer>
              .
            </div>
          </VizInfoMessageContainer>
        </ContainerBelowNav>
      </>
    );
  }

  // Webviz expects a single URL, so we point to the first OSF directory.
  // To point to images from other bags in other directories, we rewrite the
  // requested URL.
  let webvizEl: JSX.Element | null = null;

  // In first 3 if blocks below, we display customized info messages to
  // users to when
  //   1. OSFs are being processed
  //   2. OSFs processing failed
  //   3. OSFs are in a legacy format we can no longer visualize
  if (
    driveInfo.osfGenerationStatus === Status.NotStarted ||
    driveInfo.osfGenerationStatus === Status.Running
  ) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="osf-processing-message">
          We are still processing your data so you can visualize it. Please
          check back here later.{'\n'}
          {'\n'}
          If your data is still processing a couple of hours after you uploaded
          it, you can reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>{' '}
          to have them investigate.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (driveInfo.osfGenerationStatus === Status.Failed) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="osf-failed-message">
          Your data cannot be visualized because an error occurred during its
          processing.{'\n'}
          {'\n'}
          Please reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>{' '}
          to have them diagnose the issue further.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (osfType === OSFType.legacyOsf) {
    webvizEl = (
      <VizInfoMessageContainer>
        <p data-testid="legacy-osf-message">
          This data was initially processed into a legacy format that we can no
          longer visualize.{'\n'}
          {'\n'}Please reach out to the Ouster Studio team through{' '}
          <FeedbackFormContainer formLink={feedbackFormLink}>
            this form
          </FeedbackFormContainer>
          to ask them to re-process it.
        </p>
      </VizInfoMessageContainer>
    );
  } else if (
    osfPath !== null &&
    currentLocalFrameIdx !== null &&
    // With new changes, we can only display OSF V2
    osfType === OSFType.osf &&
    // There needs to be an id in the URL
    (driveId !== null || publishedDriveUuid != null)
  ) {
    if (!streamsInfo) {
      return (
        /* 
          Use the consistent loading animation
        */
        <LoaderContainer>
          <CircularProgress isIndeterminate size="50px" thickness={10} />
        </LoaderContainer>
      );
    }

    const sidebarWidth = '22.5rem';
    const toggleButtonProps: Omit<ComponentProps<typeof Button>, 'children'> = {
      variant: 'ghost',
      emphasis: 'subtle',
      color: 'content-main',
      size: 'md',
      position: 'absolute',
      top: '0.25rem',
      backgroundColor: 'background-surface-subtle',
      transitionProperty: 'opacity',
      transitionDuration: 'normal',
      tight: true,
      border: 'sm',
      borderColor: 'border-divider',
      zIndex: 'viz-area-overlay-2',
      gap: '0.25rem',
      pr: '0.5rem',
    };

    webvizEl = (
      <Box
        display="grid"
        gridTemplateRows="1fr auto auto"
        height="100%"
        overflow="hidden"
        position="relative"
      >
        <Flex
          ref={sceneContainerRef}
          transitionProperty="margin"
          transitionDuration="normal"
          transitionTimingFunction="ease-in-out"
          marginLeft={leftSidebarExpanded ? '0' : `-${sidebarWidth}`}
          position="relative"
          flex="1 1 auto"
          height="100%"
          overflow="hidden"
          flexDirection="row"
        >
          {/* Button to expand the sidebar */}
          <Button
            {...toggleButtonProps}
            left={sidebarWidth}
            onClick={() => {
              setLeftSidebarExpanded(!leftSidebarExpanded);
            }}
            borderLeftRadius={0}
            opacity={leftSidebarExpanded ? 0 : 1}
            borderTop="0"
            transformOrigin="top left"
            transform="rotate(-90deg) translateX(-100%)"
            zIndex={100}
            data-testid="expand-left-sidebar-button"
          >
            Scene
            <Icon icon="keyboardDoubleArrowLeft" transform="rotate(90deg)" />
          </Button>
          {/* 
            Previously, there are many inline functions passed as props to VizLeftSidebar
            Every time, when parent re-render, a new function will be created and passed
            to VizLeftSidebar, cause VizLeftSidebar make unnecessary re-rendering.
            To improve the playback speed, we memorize the component as well as these functions.
          */}
          {leftSidebarElement}
          <Flex flexGrow={1}>
            <VizPage
              ref={vizPageRef}
              driveInfo={driveInfo}
              pointSize={pointSize ?? 5}
              flipped={flipped}
              pointCloudGlobalSettings={pointCloudGlobalSettings}
              pointCloudSensorsSettings={pointCloudSensorsSettings}
              paused={!playing}
              hoverMode={hoverMode}
              setHoverMode={setHoverMode}
              showTransitionSpinner={
                showActiveScrubSpinner || showSeekTransitionSpinner
              }
              onVizInitialized={setIsVizInitialized}
              onCameraShareStateChange={handleCameraShareStateChange}
            />
          </Flex>
        </Flex>
        <SliderControlsContainer>
          <Box
            paddingRight="48px"
            width="100%"
            backgroundColor="background-surface-strong"
          >
            <PlayBar
              startIdx={frameStartIdx}
              endIdx={numFrames - 1}
              curIdx={currentFrameIdx}
              isMinimal={isMobilePortrait}
              rate={1}
              togglePause={handleTogglePause}
              paused={!playing}
              setIdx={(index: number) => {
                handlePlaybarStep(index);
              }}
              maxLoadedIdx={maxLoadedGlobalIdx}
              playbackSpeed={playbackSpeed}
              playbackSpeedOptions={playbackSpeedOptions} // Reverse order so top option is highest multiplier
              setPlaybackSpeed={(speed: number) => {
                setPlaybackSpeed(speed);
              }}
              isIndexing={false}
              pctIndexed={100}
              onSliderChange={handleSliderChange}
              onSliderMouseUp={handleSliderMouseUp}
              curTs={playbarTimestampMicros}
              fps={effectiveReplayFPS}
            />
          </Box>
        </SliderControlsContainer>
        {isMobileViewport ? (
          <Box position="absolute" bottom="68px" right="0" zIndex={10}>
            <VizFooter mode="exploring" py="0" isMobile={isMobileViewport} />
          </Box>
        ) : (
          <VizFooter mode="exploring" py="1em" isMobile={isMobileViewport} />
        )}
        <ModalContainer
          isOpen={isDialogOpen}
          onClose={handleModalClose}
          header="Visualization Alert"
          footer={null}
          content={
            <p style={{ maxWidth: '500px', lineHeight: '2rem' }}>
              We encountered an error while processing your recording.
              We&apos;ve done our best to recover it and help you visualize it,
              but it still might not work as expected. If you&apos;re unable to
              view your recording, feel free to share it with our support team,
              and we&apos;ll look into it.
            </p>
          }
        />
      </Box>
    );
  } else {
    webvizEl = (
      <LoaderContainer>
        <CircularProgress isIndeterminate size="50px" thickness={10} />
      </LoaderContainer>
    );
  }

  return (
    <>
      <DriveNavBar orgId={driveOrgId} />
      <ContainerBelowNav>
        <Box
          display="flex"
          height="100%"
          overflow="hidden"
          flexDirection="column"
          bg="#22272d"
          color="#ffffff"
        >
          <ShortcutKeyHandler shortcuts={shortcuts} isMac={isMacOs} />
          {webvizEl}
        </Box>
      </ContainerBelowNav>
    </>
  );
});

export { DriveViewer };
